import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Paper,
  Skeleton,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Icons
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import VerifiedIcon           from '@mui/icons-material/Verified';
import LocationOnIcon         from '@mui/icons-material/LocationOn';
import AccessTimeIcon         from '@mui/icons-material/AccessTime'; // utilisé dans les cartes formations
import WorkspacePremiumIcon   from '@mui/icons-material/WorkspacePremium';
import GroupsIcon             from '@mui/icons-material/Groups';
import SchoolIcon             from '@mui/icons-material/School';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import SecurityIcon           from '@mui/icons-material/Security';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import KeyboardArrowDownIcon  from '@mui/icons-material/KeyboardArrowDown';


// Section des avis (données de l'API, avis d'origine en secours).
import AvisSection from '../components/AvisSection';
import AvantageGroupeRabais from '../components/AvantageGroupeRabais';

import image1 from '../assets/image1.png';
import image2 from '../assets/image2.png';
import image3 from '../assets/image3.png';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const bounceY = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(8px); }
`;
// ─── Données statiques ────────────────────────────────────────────────────────
const HERO_SLIDES = [
  {
    img: image1,
    label: 'Partenaire officiel · Croix-Rouge canadienne',
    title: 'Apprenez les gestes qui sauvent des vies',
    sub:   'Formation en secourisme et RCR certifiée, pratique et accessible à Saint-Jérôme.',
  },
  {
    img: image2,
    label: 'Certifié · Croix-Rouge canadienne',
    title: 'Des formations reconnues partout au Canada',
    sub:   'Cours publics, privés et sur mesure adaptés à vos besoins et à votre emploi du temps.',
  },
  {
    img: image3,
    label: 'Sur place ou chez vous',
    title: 'Nous venons former vos équipes',
    sub:   'Déplacement dans vos locaux pour les groupes de 8 personnes et plus.',
  },
];

const FEATURES = [
  {
    icon: <WorkspacePremiumIcon sx={{ fontSize: 36 }} />,
    title: 'Certification reconnue',
    desc:  'Toutes nos formations sont certifiées par la Croix-Rouge canadienne et reconnues à l\'échelle nationale.',
    color: '#c62828',
  },
  {
    icon: <SchoolIcon          sx={{ fontSize: 36 }} />,
    title: 'Apprentissage pratique',
    desc:  'Mises en situation réelles pour que vous repartiez avec des gestes parfaitement maîtrisés.',
    color: '#1565c0',
  },
  {
    icon: <GroupsIcon          sx={{ fontSize: 36 }} />,
    title: 'Groupes & particuliers',
    desc:  'Cours publics ouverts à tous, et formations sur mesure pour entreprises dès 8 participants.',
    color: '#2e7d32',
  },
  {
    icon: <SecurityIcon        sx={{ fontSize: 36 }} />,
    title: 'Formateurs certifiés',
    desc:  'Moniteurs passionnés avec une expérience terrain significative et un engagement pédagogique fort.',
    color: '#e65100',
  },
];

// ─── Styled ───────────────────────────────────────────────────────────────────

// Hero
const HeroWrap = styled(Box)(() => ({
  position: 'relative',
  height: '100vh',
  minHeight: '600px',
  maxHeight: '900px',
  overflow: 'hidden',
}));

const HeroSlide = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease, transform 8s ease',
}));

const HeroOverlay = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(110deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.15) 100%)',
  zIndex: 2,
}));

// Barre de confiance flottante en bas du hero
const TrustBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 4,
  background: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(12px)',
  borderTop: '1px solid rgba(255,255,255,0.15)',
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(4),
  padding: theme.spacing(1.5, 3),
  flexWrap: 'wrap',
}));

// Section
const Section = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'alt',
})(({ theme, alt }) => ({
  padding: theme.spacing(9, 0),
  backgroundColor: alt ? '#f7f8fc' : '#ffffff',
}));

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const SectionLabel = styled(Typography)(() => ({
  fontSize: '0.78rem',
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#c62828',
  marginBottom: '10px',
  display: 'block',
  textAlign: 'center',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(2.5),
  marginBottom: theme.spacing(5),
  textAlign: 'center',
  fontWeight: 800,
  color: '#1a1a1a',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0, left: '50%',
    transform: 'translateX(-50%)',
    width: '44px', height: '4px',
    background: 'linear-gradient(90deg, #c62828, #ff6b6b)',
    borderRadius: '2px',
  },
}));

// Carte formation
const FormationCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(2.5),
  overflow: 'hidden',
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
  },
}));

const CardImageWrap = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
  '&:hover img': { transform: 'scale(1.05)' },
  '& img': { transition: 'transform 0.5s ease' },
}));

// Carte feature
const FeatureCard = styled(Paper)(({ theme, cardcolor }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
  height: '100%',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, height: '4px',
    background: cardcolor || '#c62828',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s ease',
  },
  '&:hover::before': { transform: 'scaleX(1)' },
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 16px 40px rgba(0,0,0,0.1)',
  },
}));

// Skeleton card
const SkeletonCard = () => (
  <Card sx={{ borderRadius: '18px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
    <Skeleton variant="rectangular" height={180} animation="wave" />
    <CardContent>
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="80%" height={24} />
      <Skeleton variant="text" width="90%" height={20} />
      <Skeleton variant="text" width="60%" height={20} sx={{ mt: 1 }} />
    </CardContent>
    <Box sx={{ p: 2, pt: 0 }}>
      <Skeleton variant="rectangular" height={36} sx={{ borderRadius: '8px' }} />
    </Box>
  </Card>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MODE_META = {
  presentiel:    { label: 'Présentiel',     color: '#2e7d32', bg: 'rgba(46,125,50,0.1)'  },
  en_ligne:      { label: 'Visioconférence',color: '#1565c0', bg: 'rgba(21,101,192,0.1)' },
  mixte:         { label: 'Mixte',          color: '#e65100', bg: 'rgba(230,81,0,0.1)'   },
  auto_formation:{ label: 'En ligne',       color: '#6a1b9a', bg: 'rgba(106,27,154,0.1)' },
};

const getMeta = (mode) => MODE_META[mode] || { label: mode, color: '#555', bg: 'rgba(0,0,0,0.06)' };

const parseArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try { return typeof val === 'string' ? JSON.parse(val) : []; }
  catch { return []; }
};

const getImageSrc = (img) => {
  if (!img) return '';
  if (img.startsWith('http') || img.startsWith('//')) return img;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
  return img.startsWith('/') ? `${base}${img}` : `${base}/${img}`;
};

const DEFAULT_IMGS = [image1, image2, image3];

// ─── Composant principal ──────────────────────────────────────────────────────
const Home = () => {
  const [slideIdx, setSlideIdx]   = useState(0);
  const [formations, setFormations] = useState([]);
  const [loading, setLoading]     = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Auto-avance hero
  useEffect(() => {
    const t = setInterval(() => setSlideIdx(p => (p + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, []);

  // Formations
  useEffect(() => {
    axios.get(`${API_URL}/formations`)
      .then(res => {
        if (res.data.success) {
          const data = res.data.data.slice(0, 6).map((f, i) => ({
            ...f,
            competences_enseignees: parseArray(f.competences_enseignees),
            imageSrc: f.image ? getImageSrc(f.image) : DEFAULT_IMGS[i % 3],
            shortDesc: f.description
              ? (f.description.length > 90 ? f.description.slice(0, 90) + '…' : f.description)
              : 'Formation certifiée en secourisme et RCR.',
          }));
          setFormations(data);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* ════════════════════════ HERO ════════════════════════════ */}
      <HeroWrap>
        {HERO_SLIDES.map((slide, i) => (
          <HeroSlide
            key={i}
            sx={{
              backgroundImage: `url(${slide.img})`,
              opacity: i === slideIdx ? 1 : 0,
              transform: i === slideIdx ? 'scale(1.04)' : 'scale(1)',
              zIndex: 1,
            }}
          />
        ))}

        <HeroOverlay />

        {/* Contenu centré verticalement */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 3,
          display: 'flex', alignItems: 'center',
        }}>
          <Wrap sx={{ width: '100%' }}>
            <Box sx={{ maxWidth: 700, animation: `${fadeUp} 0.9s ease both` }}>

              {/* Badge slide courant */}
              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                px: 2, py: 0.7,
                borderRadius: '50px',
                border: '1px solid rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.12)',
                backdropFilter: 'blur(4px)',
                mb: 3, color: '#fff',
              }}>
                <VerifiedIcon sx={{ fontSize: '0.95rem' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                  {HERO_SLIDES[slideIdx].label}
                </Typography>
              </Box>

              <Typography variant="h1" sx={{
                fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
                fontWeight: 900, color: '#fff', lineHeight: 1.1, mb: 2.5,
              }}>
                {HERO_SLIDES[slideIdx].title}
              </Typography>

              <Typography sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: 'rgba(255,255,255,0.88)',
                lineHeight: 1.8, mb: 4.5, maxWidth: 580,
              }}>
                {HERO_SLIDES[slideIdx].sub}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  component={Link} to="/formations"
                  variant="contained" size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #c62828, #8e0000)',
                    fontWeight: 800, px: 4.5, py: 1.6, borderRadius: '50px',
                    fontSize: '1rem',
                    boxShadow: '0 8px 28px rgba(198,40,40,0.4)',
                    '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)', transform: 'translateY(-2px)' },
                  }}
                >
                  Voir les formations
                </Button>
                <Button
                  component={Link} to="/about"
                  variant="outlined" size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
                    fontWeight: 700, px: 4.5, py: 1.6, borderRadius: '50px',
                    fontSize: '1rem',
                    '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Qui sommes-nous ?
                </Button>
              </Stack>
            </Box>
          </Wrap>
        </Box>

        {/* Dots navigation */}
        <Box sx={{
          position: 'absolute', bottom: 72, left: '50%', transform: 'translateX(-50%)',
          zIndex: 4, display: 'flex', gap: 1,
        }}>
          {HERO_SLIDES.map((_, i) => (
            <Box
              key={i}
              onClick={() => setSlideIdx(i)}
              sx={{
                width: i === slideIdx ? 28 : 8, height: 8,
                borderRadius: '4px',
                backgroundColor: i === slideIdx ? '#fff' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer', transition: 'all 0.35s',
              }}
            />
          ))}
        </Box>

        {/* Barre de confiance */}
        <TrustBar>
          {[
            { icon: <WorkspacePremiumIcon sx={{ fontSize: '1.1rem' }} />, text: 'Croix-Rouge canadienne'   },
            { icon: <LocationOnIcon       sx={{ fontSize: '1.1rem' }} />, text: 'Saint-Jérôme, QC'         },
            { icon: <EmojiEventsIcon      sx={{ fontSize: '1.1rem' }} />, text: 'Certification nationale'  },
            { icon: <SchoolIcon           sx={{ fontSize: '1.1rem' }} />, text: 'Formations pratiques'     },
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'rgba(255,255,255,0.85)' }}>
              {item.icon}
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </TrustBar>

        {/* Scroll indicator */}
        <Box sx={{
          position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
          zIndex: 4, animation: `${bounceY} 2.2s ease infinite`,
        }}>
          <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 28 }} />
        </Box>
      </HeroWrap>

      {/* ════════════════ POURQUOI VITA SECOURS ══════════════════ */}
      <Section alt>
        <Wrap>
          <SectionLabel>Nos engagements</SectionLabel>
          <SectionTitle variant="h3">Pourquoi choisir Vita Secours ?</SectionTitle>
          <Grid container spacing={3}>
            {FEATURES.map((f, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <FeatureCard elevation={0} cardcolor={f.color}>
                  <Box sx={{
                    width: 60, height: 60,
                    borderRadius: '16px',
                    background: `${f.color}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: f.color, mb: 2.5,
                  }}>
                    {f.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1, fontSize: '1rem' }}>
                    {f.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.75 }}>
                    {f.desc}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════════ FORMATIONS ════════════════════════════ */}
      <Section>
        <Wrap>
          <SectionLabel>Catalogue</SectionLabel>
          <SectionTitle variant="h3">Nos formations certifiées</SectionTitle>

          {loading ? (
            <Grid container spacing={3}>
              {[...Array(6)].map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                  <SkeletonCard />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {formations.map((f, i) => {
                const meta = getMeta(f.mode);
                return (
                  <Grid key={f.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormationCard component={Link} to={`/formations/${f.id}`} sx={{ textDecoration: 'none' }}>
                      <CardImageWrap>
                        <Box
                          component="img"
                          src={f.imageSrc}
                          alt={f.titre}
                          sx={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.target.src = DEFAULT_IMGS[i % 3]; }}
                        />
                        {/* Badge mode sur l'image — fond opaque pour lisibilité garantie */}
                        <Box sx={{
                          position: 'absolute', top: 12, right: 12,
                          backgroundColor: meta.color,
                          color: '#fff',
                          borderRadius: '20px',
                          px: 1.5, py: 0.45,
                          fontSize: '0.7rem', fontWeight: 700,
                          letterSpacing: '0.03em',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                          lineHeight: 1.4,
                        }}>
                          {meta.label}
                        </Box>
                      </CardImageWrap>

                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        {f.categorie && (
                          <Box sx={{
                            display: 'inline-block',
                            background: 'rgba(198,40,40,0.08)',
                            color: '#c62828',
                            px: 1.5, py: 0.3,
                            borderRadius: '20px',
                            fontSize: '0.72rem', fontWeight: 700, mb: 1.25,
                          }}>
                            {f.categorie.nom}
                          </Box>
                        )}

                        <Typography variant="h6" sx={{
                          fontWeight: 700, color: '#1a1a1a',
                          fontSize: '0.97rem', lineHeight: 1.35, mb: 1.25,
                          minHeight: '42px',
                        }}>
                          {f.titre}
                        </Typography>

                        <Typography variant="body2" sx={{
                          color: '#777', lineHeight: 1.65, mb: 2,
                          minHeight: '44px', fontSize: '0.84rem',
                        }}>
                          {f.shortDesc}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5,
                            background: 'rgba(198,40,40,0.07)',
                            px: 1.25, py: 0.4, borderRadius: '8px',
                          }}>
                            <AccessTimeIcon sx={{ fontSize: '0.85rem', color: '#c62828' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c62828' }}>
                              {f.heure}h
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#c62828', ml: 'auto' }}>
                            {f.tarif} $
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          sx={{
                            background: 'linear-gradient(135deg, #c62828, #8e0000)',
                            color: '#fff', fontWeight: 700,
                            py: 0.9, borderRadius: '10px',
                            fontSize: '0.875rem',
                            '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)' },
                          }}
                          endIcon={<ArrowForwardIcon />}
                        >
                          En savoir plus
                        </Button>
                      </CardActions>
                    </FormationCard>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Bannière inscription de groupe */}
          <Box sx={{
            mt: 5,
            mb: 2,
            p: { xs: 3, md: 4 },
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #8e0000 0%, #c62828 60%, #e53935 100%)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 3,
            boxShadow: '0 8px 32px rgba(198,40,40,0.3)',
          }}>
            <Box sx={{
              width: 56, height: 56, flexShrink: 0,
              borderRadius: '16px',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GroupsIcon sx={{ fontSize: 30, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '1rem', md: '1.1rem' }, mb: 0.5 }}>
                Vous représentez une entreprise ou un organisme ?
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.88rem', lineHeight: 1.5, mb: 1 }}>
                Inscrivez votre groupe et notre équipe vous organisera une session adaptée à vos besoins et à votre calendrier.
              </Typography>
              <AvantageGroupeRabais surFondRouge sx={{ justifyContent: 'flex-start', textAlign: 'left' }} />
            </Box>
            <Button
              component={Link}
              to="/inscription-groupe"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                flexShrink: 0,
                backgroundColor: '#fff',
                color: '#c62828',
                fontWeight: 700,
                px: 3.5, py: 1.2,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '0.9rem',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#fce4ec', transform: 'translateY(-2px)' },
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              }}
            >
              S'inscrire en groupe
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3.5 }}>
            <Button
              component={Link} to="/formations"
              variant="outlined" size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderColor: '#c62828', borderWidth: 2,
                color: '#c62828', fontWeight: 700,
                px: 5, py: 1.5, borderRadius: '50px', fontSize: '1rem',
                '&:hover': { borderWidth: 2, borderColor: '#b71c1c', background: 'rgba(198,40,40,0.05)', transform: 'translateY(-2px)' },
              }}
            >
              Voir toutes les formations
            </Button>
          </Box>
        </Wrap>
      </Section>

      {/* ══════════════════ À PROPOS ══════════════════════════════ */}
      <Section alt>
        <Wrap>
          <Grid container spacing={6} alignItems="center">

            {/* Image avec badge décoratif */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ position: 'relative', maxWidth: 480, mx: 'auto' }}>
                <Box
                  component="img"
                  src={image1}
                  alt="Académie Vita Secours"
                  sx={{
                    width: '100%',
                    borderRadius: '24px',
                    boxShadow: '0 24px 56px rgba(0,0,0,0.14)',
                    objectFit: 'cover',
                    height: { xs: '260px', md: '400px' },
                    display: 'block',
                  }}
                />
                {/* Badge rouge flottant */}
                <Box sx={{
                  position: 'absolute', bottom: -16, right: -16,
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  color: '#fff', borderRadius: '16px',
                  p: '12px 20px',
                  display: 'flex', alignItems: 'center', gap: 1,
                  boxShadow: '0 8px 28px rgba(198,40,40,0.35)',
                }}>
                  <VerifiedIcon sx={{ fontSize: '1.1rem' }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', lineHeight: 1.1, fontSize: '0.78rem' }}>
                      Partenaire officiel
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.7rem' }}>
                      Croix-Rouge canadienne
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Texte */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="overline" sx={{ color: '#c62828', fontWeight: 700, letterSpacing: '0.15em' }}>
                  Notre académie
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mt: 1, mb: 2.5, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
                  Académie Vita Secours
                </Typography>
                <Typography variant="body1" sx={{ color: '#4a4a4a', lineHeight: 1.9, fontSize: '1.05rem', mb: 2 }}>
                  École de formation en secourisme et RCR à <strong style={{ color: '#c62828' }}>Saint-Jérôme</strong>,
                  partenaire officiel de la Croix-Rouge canadienne depuis plusieurs années.
                </Typography>
                <Typography variant="body1" sx={{ color: '#4a4a4a', lineHeight: 1.9, fontSize: '1.05rem', mb: 3.5 }}>
                  Notre mission est de transmettre les bons gestes, au bon moment, afin de sauver des vies.
                  Formations certifiées et reconnues par les plus grandes institutions en secourisme au Canada.
                </Typography>

                {/* Points clés */}
                <Stack spacing={1.25} sx={{ mb: 4, alignItems: { xs: 'center', md: 'flex-start' } }}>
                  {[
                    'Cours publics, privés et sur mesure',
                    'Formateurs certifiés Croix-Rouge canadienne',
                    'Déplacement dans vos locaux (groupes de 8+)',
                  ].map((pt, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <CheckCircleIcon sx={{ color: '#c62828', fontSize: '1.1rem', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>
                        {pt}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  component={Link} to="/about"
                  variant="contained" size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #c62828, #8e0000)',
                    fontWeight: 700, px: 4.5, py: 1.5, borderRadius: '50px',
                    '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)', transform: 'translateY(-2px)' },
                  }}
                >
                  En savoir plus
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Wrap>
      </Section>

      {/* ══════════════════ BANNIÈRE CTA ══════════════════════════ */}
      {/* ══════════════════ AVIS ══════════════════ */}
      {/* Placés juste avant l'appel à l'action : la preuve sociale précède
          immédiatement la demande d'inscription. Avis gérés dans
          l'administration (page « Avis ») : bandeau défilant sur ordinateur,
          un avis à la fois sur mobile. */}
      <Section alt>
        <Wrap>
          <AvisSection
            label={<SectionLabel>Ils nous ont fait confiance</SectionLabel>}
            titre={<SectionTitle variant="h3">Ce que disent nos apprenants</SectionTitle>}
          />
        </Wrap>
      </Section>

      {/* Séparateur visuel blanc avant le CTA pour le distinguer du footer rouge */}
      <Box sx={{ backgroundColor: '#f7f8fc', py: { xs: 6, md: 8 } }}>
        <Wrap>
          <Box sx={{
            background: 'linear-gradient(135deg, #c62828 0%, #7f0000 100%)',
            borderRadius: '28px',
            py: { xs: 6, md: 8 },
            px: { xs: 3, md: 8 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(198,40,40,0.3)',
            '&::before': {
              content: '""', position: 'absolute',
              top: '-80px', right: '-80px',
              width: 320, height: 320, borderRadius: '50%',
              background: 'rgba(255,255,255,0.06)',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""', position: 'absolute',
              bottom: '-60px', left: '-60px',
              width: 240, height: 240, borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
              pointerEvents: 'none',
            },
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.15em' }}>
                Passez à l'action
              </Typography>
              <Typography variant="h3" sx={{
                fontWeight: 900, color: '#fff', mt: 1, mb: 2,
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}>
                Prêt à sauver des vies ?
              </Typography>
              <Typography sx={{
                color: 'rgba(255,255,255,0.85)', fontSize: '1.1rem',
                maxWidth: '560px', mx: 'auto', lineHeight: 1.8, mb: 4.5,
              }}>
                Inscrivez-vous dès maintenant à l'une de nos formations certifiées
                par la Croix-Rouge canadienne.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} justifyContent="center">
                <Button
                  component={Link} to="/formations"
                  variant="contained" size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: '#fff', color: '#c62828',
                    fontWeight: 800, px: 5, py: 1.7, borderRadius: '50px', fontSize: '1rem',
                    '&:hover': { background: 'rgba(255,255,255,0.92)', transform: 'translateY(-3px)' },
                  }}
                >
                  Voir les formations
                </Button>
                <Button
                  component={Link} to="/contact"
                  variant="outlined" size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
                    fontWeight: 700, px: 5, py: 1.7, borderRadius: '50px', fontSize: '1rem',
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2, borderColor: '#fff', background: 'rgba(255,255,255,0.1)', transform: 'translateY(-3px)' },
                  }}
                >
                  Nous contacter
                </Button>
              </Stack>
            </Box>
          </Box>
        </Wrap>
      </Box>
    </>
  );
};

export default Home;
